import { useCallback, useRef } from "react";
import { useOAuthPopup } from "./useOAuthPopup";
import {
  beginConnectorOAuth,
  fetchAuthorizeUrl,
} from "../services/connectors/salesforceOAuth.service";

interface StartOAuthArgs {
  catalogKey: string;
  connectionName: string;
  isSandbox?: boolean;
  /** Google Sheets-specific: threaded through to the authorize request so
   *  the resulting connection scopes to just this one file. Ignored by
   *  connectors with no such concept. */
  spreadsheetUrl?: string;
  /** Google Sheets-specific: "import_once" | "live_sync". Ignored by
   *  connectors with no such concept. */
  connectionMode?: string;
}

interface UseConnectorOAuthPopupArgs {
  /** Called with the new connection id when consent succeeds. */
  onConnected: (connectionId: string) => void;
  /** Called with a user-facing message when consent fails/cancels. */
  onError: (message: string) => void;
  /** Called when the user closes the popup before it finished. */
  onPopupClosed?: () => void;
}

interface UseConnectorOAuthPopupResult {
  authorizing: boolean;
  startOAuth: (args: StartOAuthArgs) => Promise<void>;
}

/**
 * Connector-specific (Salesforce et al.) thin wrapper around the generic
 * `useOAuthPopup` engine — preserves the original per-call `startOAuth(args)`
 * signature so existing consumers (e.g. `CredentialsStep.tsx`) don't change.
 * See `useOAuthPopup` for the shared popup/postMessage mechanics, extracted
 * here so Slack-connect can reuse them instead of duplicating this logic.
 */
export const useConnectorOAuthPopup = ({
  onConnected,
  onError,
  onPopupClosed,
}: UseConnectorOAuthPopupArgs): UseConnectorOAuthPopupResult => {
  const lastArgsRef = useRef<StartOAuthArgs | null>(null);

  const { authorizing, startOAuth: startGenericOAuth } = useOAuthPopup({
    fetchAuthorizeUrl: () => {
      const args = lastArgsRef.current;
      if (!args) throw new Error("startOAuth called without args");
      return fetchAuthorizeUrl({ ...args, mode: "popup" });
    },
    beginFullPageRedirect: () => {
      const args = lastArgsRef.current;
      if (!args) throw new Error("startOAuth called without args");
      return beginConnectorOAuth(args);
    },
    onConnected: (data) => {
      if (typeof data.connection_id === "string" && data.connection_id.trim().length > 0) {
        onConnected(data.connection_id);
      } else {
        onError("Connection failed. Please try again.");
      }
    },
    onError,
    onPopupClosed,
  });

  const startOAuth = useCallback(
    async (args: StartOAuthArgs) => {
      lastArgsRef.current = args;
      await startGenericOAuth();
    },
    [startGenericOAuth],
  );

  return { authorizing, startOAuth };
};

export default useConnectorOAuthPopup;
